import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/** Small confirm/form dialog. Renders in a portal above drawers (z-[100000]). */
export default function Modal({ open, title, onClose, children, footer, zClass = 'z-[100000]', wide = false }: {
  open: boolean; title: string; onClose: () => void; children: ReactNode; footer?: ReactNode; zClass?: string; wide?: boolean;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className={`fixed inset-0 ${zClass} flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm`} onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()} className={`w-full ${wide ? "max-w-4xl" : "max-w-md"} max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl`}>
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
        </div>
        <div className="px-5 py-4 space-y-3">{children}</div>
        {footer && <div className="px-5 py-4 border-t border-gray-100 flex justify-end gap-2">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}

export const btnPrimary = 'px-4 py-2 rounded-full text-xs font-medium text-white bg-primary hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition';
export const btnGhost = 'px-4 py-2 rounded-full text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 transition';
export const btnDanger = 'px-4 py-2 rounded-full text-xs font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition';
export const inputCls = 'w-full px-3 py-2 text-xs border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent';
export const labelCls = 'block text-xs font-medium text-gray-700 mb-1';
